/**
 * A composable dbId set — the single input form the query-driven visibility,
 * selection and theming APIs accept (alongside a bare dbId / dbId[] for the
 * legacy value form). Each independently-settable state — an isolation, a
 * selection, or one theming colour — is modelled as a `base` plus explicit
 * dbId edits:
 *
 *     resolved = (base ∪ added) \ removed
 *
 * The base is a Query (`{ kind: 'query', query }`); `added` / `removed` are
 * explicit dbId edits layered on top via {@link add} / {@link remove}. A flat
 * set (no base) is just an explicit dbId set.
 *
 * Construct one ergonomically with the static factories and chain edits:
 *
 *     CompositeSet.query("SELECT dbId FROM pdb WHERE category = 'Walls'")
 *     CompositeSet.ids([1, 2, 3])
 *     CompositeSet.query(doorsSql).add([9]).remove([3])
 *
 * The set records intent only; resolving it to dbIds runs the query on demand
 * (a query base is re-evaluated on every resolve, so it always reflects the
 * model's current property data rather than a cached snapshot).
 *
 * A set passed to a surface either replaces the live state (`overwrite`) or is
 * layered onto it (`compose`, via {@link composeWith}). Either way the surface
 * reports the `{ added, removed }` dbId delta back to the caller.
 *
 * @private
 */
/** @typedef {{ kind: 'query', query: string }} CompositeBase */

/** Coerces a dbId or dbId[] to an array. */
function toDbIdArray(dbIds) {
  return Array.isArray(dbIds) ? dbIds : [dbIds];
}

/**
 * Runs a PDBSQL query against `model` and returns the dbIds of every object it
 * matches. The query runs in `dbIdOnly` mode, so its projection is irrelevant —
 * `SELECT dbId …`, `SELECT *`, or any other columns all resolve to the matching
 * dbIds. Throws for a blank query or a missing model; a model that can't
 * evaluate the query rejects with a coded error (`UNKNOWN_COLUMNS`, …) which
 * propagates unchanged. A query matching zero rows resolves to an empty array.
 *
 * When `lenientColumns` is set, a column the model lacks resolves to no match
 * (the query runs against whatever columns the model DOES have) instead of
 * rejecting with `UNKNOWN_COLUMNS`. This is what lets a multi-model fan-out
 * match each model on its available columns rather than aborting the ones
 * missing a referenced column. Default (flag absent) keeps the strict
 * `UNKNOWN_COLUMNS` behavior for single-model typo protection.
 *
 * @private
 * @param {import("../application/Model").Model} model
 * @param {string} query
 * @param {Object} [opts]
 * @param {boolean} [opts.lenientColumns=false]
 * @returns {Promise<number[]>}
 */
async function resolveQueryToDbIds(model, query, { lenientColumns = false } = {}) {
  if (typeof query !== 'string' || !query.trim()) {
    throw new Error('Query must be a non-empty string.');
  }
  if (!model) {
    throw new Error('Cannot run a query without a model.');
  }
  // Only surface lenientColumns to the query layer when it's actually opted
  // into — the default call passes just { dbIdOnly: true }, keeping the strict
  // UNKNOWN_COLUMNS behavior (and not perturbing callers that inspect options).
  const options = { dbIdOnly: true };
  if (lenientColumns) options.lenientColumns = true;
  const result = await model.query(query, options);
  return result.rows.map((r) => r[0]);
}

/** @private */
export class CompositeSet {
  /**
   * The base the set composes over, or null when the set is flat (no base).
   * @private
   * @type {CompositeBase | null}
   */
  base = null;

  /** dbIds added on top of the base. @private @type {Set<number>} */
  added = new Set();

  /** dbIds removed from the base. @private @type {Set<number>} */
  removed = new Set();

  /**
   * A set whose base is the given PDBSQL query.
   * @private
   * @param {string} query
   * @returns {CompositeSet}
   */
  static query(query) {
    return new CompositeSet().setBase({ kind: 'query', query });
  }

  /**
   * A flat set of explicit dbIds (no base).
   * @private
   * @param {number|number[]} dbIds
   * @returns {CompositeSet}
   */
  static ids(dbIds) {
    return new CompositeSet().add(toDbIdArray(dbIds));
  }

  /**
   * True if `x` is a CompositeSet — the test the surfaces use to tell the
   * query-driven input form apart from a bare dbId / dbId[].
   * @private
   * @param {*} x
   * @returns {x is CompositeSet}
   */
  static is(x) {
    return x instanceof CompositeSet;
  }

  /**
   * Where the set's membership comes from: `'query'` while a query base is
   * set, `'flat'` when there is no base.
   * @private
   * @returns {'query'|'flat'}
   */
  get provenance() {
    return this.base ? this.base.kind : 'flat';
  }

  /**
   * Replaces the base, dropping any prior edits. Returns `this` for chaining.
   * @private
   * @param {CompositeBase} base
   * @returns {this}
   */
  setBase(base) {
    this.base = base;
    this.added.clear();
    this.removed.clear();
    return this;
  }

  /**
   * Collapses the set to flat: drops the base and all edits. Used by the
   * value forms (isolate(dbIds), select(dbIds), showAll, …) — afterwards the
   * set no longer claims to reflect a query. Returns `this`.
   * @private
   * @returns {this}
   */
  clear() {
    this.base = null;
    this.added.clear();
    this.removed.clear();
    return this;
  }

  /**
   * Adds dbIds on top of the base (and un-removes any that were removed).
   * Returns `this` for chaining.
   * @private
   * @param {number|number[]} dbIds
   * @returns {this}
   */
  add(dbIds) {
    for (const id of toDbIdArray(dbIds)) {
      this.added.add(id);
      this.removed.delete(id);
    }
    return this;
  }

  /**
   * Removes dbIds from the set (and un-adds any that were added). Returns
   * `this` for chaining.
   * @private
   * @param {number|number[]} dbIds
   * @returns {this}
   */
  remove(dbIds) {
    for (const id of toDbIdArray(dbIds)) {
      this.removed.add(id);
      this.added.delete(id);
    }
    return this;
  }

  /**
   * A defensive copy of the base descriptor, or null when the set is flat.
   * @private
   * @returns {CompositeBase | null}
   */
  getBase() {
    return this.base ? { ...this.base } : null;
  }

  /**
   * An independent copy of this set — used by a surface to store a passed set
   * as its live state without aliasing the caller's object.
   * @private
   * @returns {CompositeSet}
   */
  clone() {
    const copy = new CompositeSet();
    copy.base = this.base ? { ...this.base } : null;
    copy.added = new Set(this.added);
    copy.removed = new Set(this.removed);
    return copy;
  }

  /**
   * Layers `other` onto this set in place — the `compose` (non-overwrite)
   * operation. `other`'s positive members (its resolved base ∪ its `added`)
   * are added to this set, and `other`'s `removed` are removed from it, so a
   * single composing call can both add to and subtract from the live state.
   *
   * `other`'s base is resolved to concrete dbIds at compose time and folded
   * into this set's `added`; this set keeps its own base, so only this set's
   * base stays live-resolving (you cannot stack two re-resolving query bases).
   *
   * @private
   * @param {import("../application/Model").Model} model
   * @param {CompositeSet} other
   * @param {Object} [opts]
   * @param {boolean} [opts.lenientColumns=false] - forward to `other`'s query
   *   base; a column the model lacks resolves to no match instead of erroring.
   * @returns {Promise<void>}
   */
  async composeWith(model, other, { lenientColumns = false } = {}) {
    const positive = new Set(await other.resolveBase(model, { lenientColumns }));
    for (const id of other.added) positive.add(id);
    this.add(Array.from(positive));
    this.remove(Array.from(other.removed));
  }

  /**
   * Resolves the base to its dbIds against `model`. The query base re-runs the
   * query each time. A flat set has no base, so resolves to the empty array.
   *
   * @private
   * @param {import("../application/Model").Model} model
   * @param {Object} [opts]
   * @param {boolean} [opts.lenientColumns=false] - forward to the query; a
   *   column the model lacks resolves to no match instead of erroring.
   * @returns {Promise<number[]>}
   */
  async resolveBase(model, { lenientColumns = false } = {}) {
    if (!this.base) return [];
    return resolveQueryToDbIds(model, this.base.query, { lenientColumns });
  }

  /**
   * Resolves the full set — `(base ∪ added) \ removed` — to dbIds.
   *
   * @private
   * @param {import("../application/Model").Model} model
   * @param {Object} [opts]
   * @param {boolean} [opts.lenientColumns=false] - forward to the query base; a
   *   column the model lacks resolves to no match instead of erroring.
   * @returns {Promise<number[]>}
   */
  async resolve(model, { lenientColumns = false } = {}) {
    const set = new Set(await this.resolveBase(model, { lenientColumns }));
    for (const id of this.added) set.add(id);
    for (const id of this.removed) set.delete(id);
    return Array.from(set);
  }
}

/**
 * The dbId delta between a previous and a next resolved set — what a
 * query-driven call reports back to the caller. `added` is the dbIds in `next`
 * but not `prev`; `removed` is the dbIds in `prev` but not `next`.
 *
 * @private
 * @param {Iterable<number>} prev - dbIds resolved before the operation.
 * @param {Iterable<number>} next - dbIds resolved after the operation.
 * @returns {{ added: number[], removed: number[] }}
 */
export function dbIdDelta(prev, next) {
  const prevSet = prev instanceof Set ? prev : new Set(prev);
  const nextSet = next instanceof Set ? next : new Set(next);
  const added = [];
  const removed = [];
  for (const id of nextSet) if (!prevSet.has(id)) added.push(id);
  for (const id of prevSet) if (!nextSet.has(id)) removed.push(id);
  return { added, removed };
}